import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from 'sonner';
import './App.css';
import { SiteSettingsProvider, useSiteSettings } from './context/SiteSettingsContext';
import Header from './components/Header';
import Hero from './components/Hero';
import WhyChooseUs from './components/WhyChooseUs';
import VehicleInventory from './components/VehicleInventory';
import Testimonials from './components/Testimonials';
import HappyCustomers from './components/HappyCustomers';
import Financing from './components/Financing';
import Footer from './components/Footer';
import VehicleDetail from './components/VehicleDetail';
import InventoryPage from './pages/InventoryPage';

// New Feature Components
import TrustBadges from './components/TrustBadges';
import SecurityBadges from './components/SecurityBadges';
import FacebookMessenger from './components/FacebookMessenger';
import ExitIntentPopup from './components/ExitIntentPopup';
import ClickToCall from './components/ClickToCall';
import SpecialOffersBanner from './components/SpecialOffersBanner';
import VehicleComparison from './components/VehicleComparison';
import LoanCalculator from './components/LoanCalculator';
import { configureAnalytics, installClickTracking, trackPageView } from './lib/analytics';
import ConsentBanner from './components/ConsentBanner';
import { HomepageSEO } from './components/SEOComponents';

// Admin Pages (lazy loaded - not needed for public users)
const AdminLogin = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminLogin })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const AdminVehicleList = lazy(() => import('./pages/admin/AdminVehicles').then(m => ({ default: m.AdminVehicleList })));
const AdminVehicleForm = lazy(() => import('./pages/admin/AdminVehicles').then(m => ({ default: m.AdminVehicleForm })));
const AdminInquiries = lazy(() => import('./pages/admin/AdminInquiries').then(m => ({ default: m.AdminInquiries })));
const AdminFinancing = lazy(() => import('./pages/admin/AdminFinancing').then(m => ({ default: m.AdminFinancing })));
const AdminSiteSettings = lazy(() => import('./pages/admin/AdminSiteSettingsAdvanced').then(m => ({ default: m.AdminSiteSettings })));
const AdminHappyCustomers = lazy(() => import('./pages/admin/AdminHappyCustomers').then(m => ({ default: m.AdminHappyCustomers })));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials').then(m => ({ default: m.AdminTestimonials })));
const AdminFeaturesSettings = lazy(() => import('./pages/admin/AdminFeaturesSettings'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminMarketing = lazy(() => import('./pages/admin/AdminMarketing'));
const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));
const AdminBulkImport = lazy(() => import('./pages/admin/AdminBulkImport'));
const AdminInventorySync = lazy(() => import('./pages/admin/AdminInventorySync'));
const AdminReferrals = lazy(() => import('./pages/admin/AdminReferrals'));
const AdminPushNotifications = lazy(() => import('./pages/admin/AdminPushNotifications'));
const AdminPromotions = lazy(() => import('./pages/admin/AdminPromotions'));
const AdminSalesDashboard = lazy(() => import('./pages/admin/AdminSalesDashboard'));
const AdminAppointments = lazy(() => import('./pages/admin/AdminAppointments'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));

// Secondary pages (lazy loaded)
const AboutPage = lazy(() => import('./pages/AboutPage'));
const FinancingPage = lazy(() => import('./pages/FinancingPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const PromoPage = lazy(() => import('./pages/PromoPage'));
const BookTestDrivePage = lazy(() => import('./pages/BookTestDrivePage'));
const SegwayPage = lazy(() => import('./pages/SegwayPage'));
const SegwayModelPage = lazy(() => import('./pages/SegwayModelPage'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const isAuth = localStorage.getItem('admin_auth') === 'true';
  const token = localStorage.getItem('admin_token');
  
  if (!isAuth || !token) {
    return <Navigate to="/admin/login" replace />;
  }
  
  return children;
};

// Promotional Banner Component
const PromoBanner = () => {
  const { settings } = useSiteSettings();
  
  if (!settings.bannerEnabled || !settings.bannerText) return null;
  
  const bannerContent = (
    <div 
      className="py-2 px-4 text-center text-white text-sm font-medium"
      style={{ backgroundColor: settings.bannerColor || '#dc2626' }}
    >
      {settings.bannerText}
    </div>
  );
  
  if (settings.bannerLink) {
    return (
      <Link to={settings.bannerLink}>
        {bannerContent}
      </Link>
    );
  }
  
  return bannerContent;
};

const Home = () => {
  const { settings } = useSiteSettings();
  
  return (
    <div className="min-h-screen bg-white">
      <HomepageSEO baseUrl={window.location.origin} />
      <SpecialOffersBanner />
      <PromoBanner />
      <Header />
      <main>
        {settings.showHero !== false && <Hero />}
        <TrustBadges />
        {settings.showWhyChooseUs !== false && <WhyChooseUs />}
        {settings.showHappyCustomers !== false && <HappyCustomers />}
        {settings.showInventory !== false && <VehicleInventory />}
        <SecurityBadges />
        {settings.showTestimonials !== false && <Testimonials />}
        {settings.showFinancing !== false && <Financing />}
        <LoanCalculator />
      </main>
      <Footer />
      
      {/* Global Components */}
      <FacebookMessenger pageId="bigchiefmotors" />
      <ClickToCall />
      <VehicleComparison />
      <ExitIntentPopup />
    </div>
  );
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

// Page views (path + query) for first-party analytics, GA4 and Meta; skips /admin.
const AnalyticsTracker = () => {
  const { pathname, search } = useLocation();
  const { settings, loading } = useSiteSettings();

  useEffect(() => { installClickTracking(); }, []);
  useEffect(() => { if (!loading) configureAnalytics(settings); }, [loading, settings]);
  // Deferred a tick so react-helmet has set the new page title.
  useEffect(() => { const t = setTimeout(trackPageView, 50); return () => clearTimeout(t); }, [pathname, search]);

  return null;
};

const LazyFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <SiteSettingsProvider>
        <div className="App">
          <Toaster richColors position="top-right" closeButton />
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <Suspense fallback={<LazyFallback />}>
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/vehicles" element={<InventoryPage key="vehicles" lockedCategory="Vehicles" />} />
            <Route path="/powersports" element={<InventoryPage key="powersports" lockedCategory="Power Sports" />} />
            <Route path="/marine" element={<InventoryPage key="marine" lockedCategory="Marine" />} />
            <Route path="/vehicle/:id" element={<VehicleDetail />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/apply-now" element={<FinancingPage />} />
          <Route path="/financing" element={<Navigate to="/apply-now" replace />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/promo/:slug" element={<PromoPage />} />
          <Route path="/book-test-drive" element={<BookTestDrivePage />} />
          <Route path="/segway" element={<SegwayPage />} />
          <Route path="/segway/:slug" element={<SegwayModelPage />} />
          
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/vehicles" element={
            <ProtectedRoute>
              <AdminVehicleList />
            </ProtectedRoute>
          } />
          <Route path="/admin/vehicles/new" element={
            <ProtectedRoute>
              <AdminVehicleForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/vehicles/edit/:id" element={
            <ProtectedRoute>
              <AdminVehicleForm />
            </ProtectedRoute>
          } />
          <Route path="/admin/happy-customers" element={
            <ProtectedRoute>
              <AdminHappyCustomers />
            </ProtectedRoute>
          } />
          <Route path="/admin/testimonials" element={
            <ProtectedRoute>
              <AdminTestimonials />
            </ProtectedRoute>
          } />
          <Route path="/admin/inquiries" element={
            <ProtectedRoute>
              <AdminInquiries />
            </ProtectedRoute>
          } />
          <Route path="/admin/financing" element={
            <ProtectedRoute>
              <AdminFinancing />
            </ProtectedRoute>
          } />
          <Route path="/admin/settings" element={
            <ProtectedRoute>
              <AdminSiteSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/features" element={
            <ProtectedRoute>
              <AdminFeaturesSettings />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/marketing" element={
            <ProtectedRoute>
              <AdminMarketing />
            </ProtectedRoute>
          } />
          <Route path="/admin/email-templates" element={
            <ProtectedRoute>
              <AdminEmailTemplates />
            </ProtectedRoute>
          } />
          <Route path="/admin/bulk-import" element={
            <ProtectedRoute>
              <AdminBulkImport />
            </ProtectedRoute>
          } />
          <Route path="/admin/inventory-sync" element={
            <ProtectedRoute>
              <AdminInventorySync />
            </ProtectedRoute>
          } />
          <Route path="/admin/referrals" element={
            <ProtectedRoute>
              <AdminReferrals />
            </ProtectedRoute>
          } />
          <Route path="/admin/push-notifications" element={
            <ProtectedRoute>
              <AdminPushNotifications />
            </ProtectedRoute>
          } />
          <Route path="/admin/promotions" element={
            <ProtectedRoute>
              <AdminPromotions />
            </ProtectedRoute>
          } />
          <Route path="/admin/sales" element={
            <ProtectedRoute>
              <AdminSalesDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/appointments" element={
            <ProtectedRoute>
              <AdminAppointments />
            </ProtectedRoute>
          } />
          <Route path="/admin/users" element={
            <ProtectedRoute>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/audit-log" element={
            <ProtectedRoute>
              <AdminAuditLog />
            </ProtectedRoute>
          } />
          <Route path="/admin/dashboard" element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Home />} />
          </Routes>
          </Suspense>
            <ConsentBanner />
          </BrowserRouter>
        </div>
      </SiteSettingsProvider>
    </HelmetProvider>
  );
}

export default App;
